import React, { useRef, useCallback } from 'react'

import { Collapse } from '@enuygun/design-system'
import { ArrowDown, ArrowUp } from '@enuygun/icons'
import { useTranslation } from 'next-i18next'
import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

import * as S from './Faq.styled'

type FaqPanelTitleProps = {
  isOpen: boolean
  question: string
  answer: string
  questionIndex: number
  market: string
  onFaqClick: (questionIndex: number, question: string, answer: string, isOpen: boolean) => void
}

const FaqPanelTitle = ({ isOpen, question, answer, questionIndex, market, onFaqClick }: FaqPanelTitleProps) => {
  const brand = market === 'tr' ? 'enuygun' : 'wingie'

  return (
    <S.FaqQuesiton data-testid={`${brand}-homepage-faq-question-${questionIndex}-title`} onClick={() => onFaqClick(questionIndex, question, answer, !isOpen)}>
      <div data-testid={`${brand}-homepage-faq-question-${questionIndex}-question`}>{question}</div>
      <S.FaqIcon>{isOpen ? <ArrowUp width={20} height={20} /> : <ArrowDown width={20} height={20} />}</S.FaqIcon>
    </S.FaqQuesiton>
  )
}

const createFaqPanelTitle = ({ question, answer, questionIndex, market, onFaqClick }: Omit<FaqPanelTitleProps, 'isOpen'>) => {
  const FaqPanelTitleRenderer = ({ isOpen }: { isOpen?: boolean }) => (
    <FaqPanelTitle isOpen={!!isOpen} question={question} answer={answer} questionIndex={questionIndex} market={market} onFaqClick={onFaqClick} />
  )
  FaqPanelTitleRenderer.displayName = 'FaqPanelTitleRenderer'
  return FaqPanelTitleRenderer
}

const Faq = ({ res }) => {
  const { t } = useTranslation('common')
  const { market } = useAppState()
  const { isMobile, isTablet } = useDeviceType()
  const faqContainerRef = useRef<HTMLDivElement>(null)
  const isWingie = market !== 'tr'
  const faqList = res?.[3]?.faqList
  const brand = market === 'tr' ? 'enuygun' : 'wingie'

  // Faq question click tracking (accordion expand/collapse)
  const handleFaqClick = useCallback(
    (questionIndex: number, question: string, answer: string, isOpen: boolean) => {
      const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        type: 'click',
        message: 'faq_question_click',
        environment: environment,
        requestId,
        questionIndex: questionIndex + 1,
        questionText: question || null,
        answerText: answer || null,
        isExpanded: isOpen,
        isMobile: !!(isMobile || isTablet),
        market: market,
      })
    },
    [isMobile, isTablet, market, isWingie]
  )

  if (!faqList?.length) {
    return null
  }

  return (
    <S.FaqContainer ref={faqContainerRef} data-testid={`${brand}-homepage-faq-container`}>
      <S.FaqHeader data-testid={`${brand}-homepage-faq-header`}>{t('Faq.heading')}</S.FaqHeader>
      <S.Faq data-testid={`${brand}-homepage-faq`}>
        <Collapse forceRender accordion defaultActiveKey={0} variant="border">
          {faqList.map(({ question, answer }, key) => (
            <Collapse.Panel
              shape="default"
              key={answer}
              forceRender
              dataTestId={`${brand}-homepage-faq-question-${key}`}
              title={createFaqPanelTitle({
                question: question ?? '',
                answer: answer ?? '',
                questionIndex: key,
                market: market ?? '',
                onFaqClick: handleFaqClick,
              })}
            >
              <S.FaqAnswer data-testid={`${brand}-homepage-faq-answer-${key}`}>{answer}</S.FaqAnswer>
            </Collapse.Panel>
          ))}
        </Collapse>
      </S.Faq>
    </S.FaqContainer>
  )
}

export default Faq
